import React, { useMemo, useState, useRef } from "react";

const CHUNK_SIZE = 200;

const BASE_URL = "https://mis.empowerplus.in";

const MODULES = [
    { label: "Centers", endpoint: "centers", field: "centerId", placeholder: "EP/TC-A00001" },
    { label: "Assessors", endpoint: "assessors", field: "assessorId", placeholder: "EP/AS-A00001" },
    { label: "Batches", endpoint: "batches", field: "epBatchId", placeholder: "EP/2025-26/CSR/B001" },
    { label: "Courses", endpoint: "courses", field: "courseId", placeholder: "EP/2025-26/CSR/Course/001" },
    { label: "Trainers", endpoint: "trainers", field: "trainerId", placeholder: "EP/TR-A00001" },
    { label: "Trainer Master", endpoint: "trainer-master", field: "trainerId", placeholder: "EP/TR-A00001" },
    { label: "Mobilizations", endpoint: "mobilizations", field: "beneficiaryId", placeholder: "BEN-00001" },
    { label: "Candidates", endpoint: "candidates", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Attendance", endpoint: "attendance", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "External Assessment", endpoint: "external-assessment", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Training Completion", endpoint: "training-completion", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Certificates", endpoint: "certificates", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Internal Assessment", endpoint: "internal-assessment", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Placements", endpoint: "placements", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Candidate Retention", endpoint: "candidate-retention", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "JioBP HC Feedback", endpoint: "jiobp-healthcamp-feedback", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Enrollment Verification", endpoint: "enrollment-verification", field: "beneficiaryId", placeholder: "BEN-00001" },
    { label: "Health Camp", endpoint: "health-camp", field: "beneficiaryId", placeholder: "BEN-00001" },
    { label: "Center Batch Management", endpoint: "center-batch-management", field: "cbId", placeholder: "EP/CB-A00001" },
    { label: "Batch Trainer Management", endpoint: "batch-trainer-management", field: "btId", placeholder: "EP/BT-A00001" },
    { label: "Course Trainer Management", endpoint: "course-trainer-management", field: "ctId", placeholder: "EP/CT-A00001" },
    { label: "Center Assessor Management", endpoint: "center-assessor-management", field: "caId", placeholder: "EP/CA-A00001" },
    { label: "Project Center Management", endpoint: "project-center-management", field: "pcId", placeholder: "EP/PC-A00001" },
    { label: "Pre Post Scores", endpoint: "pre-post-scores", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
    { label: "Enterprise Support", endpoint: "enterprise-support", field: "beneficiaryId", placeholder: "EP/BEN-00001" },
];

export default function BulkDeletePage() {
    const token = localStorage.getItem("token");

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
        [token]
    );

    const [moduleIdx, setModuleIdx] = useState(0);
    const [idText, setIdText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [progress, setProgress] = useState(null); // { done, total, deleted, notFound }
    const abortRef = useRef(false);

    const selectedModule = MODULES[moduleIdx];

    const parseIds = () =>
        idText
            .split(/[\n,]+/)
            .map((s) => s.trim())
            .filter(Boolean);

    const handleDelete = async () => {
        const ids = parseIds();
        if (!ids.length) { setError("Enter at least one ID"); return; }

        const confirmed = window.confirm(
            `Delete ${ids.length} record(s) from "${selectedModule.label}"?\n\nThis action cannot be undone.`
        );
        if (!confirmed) return;

        setDeleting(true);
        setResult(null);
        setError("");
        abortRef.current = false;

        const chunks = [];
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) chunks.push(ids.slice(i, i + CHUNK_SIZE));

        let totalDeleted = 0;
        let allNotFound = [];
        let done = 0;
        setProgress({ done: 0, total: ids.length, deleted: 0, notFound: [] });

        try {
            for (const chunk of chunks) {
                if (abortRef.current) break;

                const res = await fetch(`${BASE_URL}/api/bulk-delete/${selectedModule.endpoint}`, {
                    method: "DELETE",
                    headers: authHeaders,
                    body: JSON.stringify(chunk),
                });

                const text = await res.text();
                let data;
                try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }

                if (!res.ok) {
                    setError(data?.message || `Request failed (${res.status})`);
                    setDeleting(false);
                    setProgress(null);
                    return;
                }

                totalDeleted += data.deleted ?? 0;
                allNotFound = [...allNotFound, ...(data.notFound ?? [])];
                done += chunk.length;
                setProgress({ done, total: ids.length, deleted: totalDeleted, notFound: allNotFound });
            }

            setResult({ deleted: totalDeleted, notFound: allNotFound, ids });
            setIdText("");
        } catch (err) {
            console.error("[BulkDelete] Error:", err);
            setError("Request failed — check console");
        } finally {
            setDeleting(false);
            setProgress(null);
        }
    };

    const ids = parseIds();

    return (
        <div className="bd-page">
            <h1>Bulk Delete</h1>

            {/* Module selector */}
            <div className="card">
                <div className="card-title">1. Select Module</div>
                <div className="module-grid">
                    {MODULES.map((m, i) => (
                        <button
                            key={m.endpoint}
                            className={`module-btn${moduleIdx === i ? " active" : ""}`}
                            onClick={() => { setModuleIdx(i); setResult(null); setError(""); }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ID input */}
            <div className="card">
                <div className="card-title">
                    2. Enter {selectedModule.field} values — one per line or comma-separated
                </div>
                <textarea
                    className="id-input"
                    placeholder={`e.g.\n${selectedModule.placeholder}\n${selectedModule.placeholder.replace(/\d+$/, "00002")}`}
                    value={idText}
                    onChange={(e) => { setIdText(e.target.value); setResult(null); setError(""); }}
                    rows={8}
                />
                <div className="id-count">
                    {ids.length > 0 ? `${ids.length} ID${ids.length !== 1 ? "s" : ""} detected` : "No IDs entered"}
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                    <button
                        className="btn btn-danger-solid"
                        onClick={handleDelete}
                        disabled={deleting || !ids.length}
                    >
                        {deleting ? "Deleting..." : `Delete ${ids.length || ""} Record${ids.length !== 1 ? "s" : ""}`}
                    </button>
                    {deleting && (
                        <button
                            className="btn"
                            style={{ borderColor: "#dc2626", color: "#dc2626" }}
                            onClick={() => { abortRef.current = true; }}
                        >
                            Cancel
                        </button>
                    )}
                    {!deleting && (
                        <button
                            className="btn"
                            onClick={() => { setIdText(""); setResult(null); setError(""); }}
                        >
                            Clear
                        </button>
                    )}
                </div>

                {/* Progress bar */}
                {progress && (
                    <div style={{ marginTop: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, marginBottom: 6, color: "#374151" }}>
                            <span>Processing... {progress.done} / {progress.total} IDs</span>
                            <span style={{ color: "#065f46" }}>{progress.deleted} deleted</span>
                        </div>
                        <div style={{ background: "#e5e7eb", borderRadius: 99, height: 10, overflow: "hidden" }}>
                            <div style={{
                                height: "100%",
                                borderRadius: 99,
                                background: "linear-gradient(90deg, #2563eb, #16a34a)",
                                width: `${Math.round((progress.done / progress.total) * 100)}%`,
                                transition: "width 0.3s ease",
                            }} />
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, fontWeight: 600 }}>
                            {Math.round((progress.done / progress.total) * 100)}% complete
                        </div>
                    </div>
                )}
            </div>

            {/* Error */}
            {error && (
                <div className="flash flash-err">{error}</div>
            )}

            {/* Result */}
            {result && (
                <div className="card">
                    <div className="card-title">Result — {selectedModule.label}</div>

                    <div className="result-stats">
                        <div className="stat stat-ok">
                            <div className="stat-num">{result.deleted ?? 0}</div>
                            <div className="stat-label">Deleted</div>
                        </div>
                        <div className="stat stat-warn">
                            <div className="stat-num">{result.notFound?.length ?? 0}</div>
                            <div className="stat-label">Not Found</div>
                        </div>
                        <div className="stat">
                            <div className="stat-num">{result.ids?.length ?? 0}</div>
                            <div className="stat-label">Submitted</div>
                        </div>
                    </div>

                    {result.notFound?.length > 0 && (
                        <div className="not-found-box">
                            <div className="not-found-title">IDs not found (already deleted or never existed):</div>
                            <div className="not-found-list">
                                {result.notFound.map((id) => (
                                    <span key={id} className="id-chip">{id}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <style>{`
.bd-page { padding: 22px; background: #f7f7fb; min-height: 100vh; font-family: Inter, system-ui; }
h1 { margin: 0 0 16px; font-size: 26px; font-weight: 900; }

.card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 18px; margin-bottom: 14px; }
.card-title { font-size: 14px; font-weight: 900; margin-bottom: 12px; color: #111827; }

.module-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.module-btn {
    border: 1px solid #e5e7eb; background: #fff; padding: 8px 14px;
    border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer; color: #374151;
    transition: all 0.15s;
}
.module-btn:hover { border-color: #2563eb; color: #2563eb; }
.module-btn.active { background: #2563eb; color: #fff; border-color: #2563eb; }

.id-input {
    width: 100%; box-sizing: border-box; border: 1px solid #e5e7eb;
    border-radius: 12px; padding: 12px; font-size: 13px; font-family: monospace;
    resize: vertical; outline: none; line-height: 1.6;
}
.id-input:focus { border-color: #2563eb; }

.id-count { font-size: 12px; color: #6b7280; margin-top: 6px; font-weight: 700; }

.btn {
    border: 1px solid #e5e7eb; background: #fff; padding: 10px 18px;
    border-radius: 12px; font-weight: 800; cursor: pointer; font-size: 13px;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-danger-solid { background: #dc2626; color: #fff; border-color: #dc2626; }
.btn-danger-solid:hover:not(:disabled) { background: #b91c1c; }

.flash { padding: 10px 14px; border-radius: 10px; margin-bottom: 14px; font-weight: 700; font-size: 13px; }
.flash-err { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

.result-stats { display: flex; gap: 12px; margin-bottom: 14px; }
.stat {
    flex: 1; border: 1px solid #e5e7eb; border-radius: 12px;
    padding: 14px; text-align: center;
}
.stat-ok { border-color: #a7f3d0; background: #ecfdf5; }
.stat-warn { border-color: #fde68a; background: #fffbeb; }
.stat-num { font-size: 28px; font-weight: 900; }
.stat-ok .stat-num { color: #065f46; }
.stat-warn .stat-num { color: #92400e; }
.stat-label { font-size: 12px; font-weight: 700; color: #6b7280; margin-top: 4px; }

.not-found-box {
    border: 1px solid #fde68a; background: #fffbeb;
    border-radius: 12px; padding: 14px;
}
.not-found-title { font-size: 12px; font-weight: 900; color: #92400e; margin-bottom: 10px; }
.not-found-list { display: flex; flex-wrap: wrap; gap: 6px; }
.id-chip {
    background: #fff; border: 1px solid #f59e0b; color: #92400e;
    padding: 4px 10px; border-radius: 8px; font-size: 12px; font-family: monospace; font-weight: 700;
}
            `}</style>
        </div>
    );
}
